import { Button, Flex, FormControl, FormErrorMessage, FormLabel, HStack, Input, Stack, useToast, VStack } from '@chakra-ui/react'
import React from 'react'
import { Controller, useForm } from 'react-hook-form'
import { BsArrowCounterclockwise, BsBoxArrowInDown } from 'react-icons/bs'
import User, { addNewUser } from '../../classes/User'
import { useUserContext } from '../../context/userContext'
import { toastErrDesc, toastVal } from '../../utils/toastUtils'
import { InputIDCard, InputPassword, InputTelephoneNumber, SectionForm, SelectLevel, SelectOffice, SelectTitleName, SelectUserRole } from '../shared'

const AddUserForm = () => {
    const defaultValues = {
        username: "",
        title: null,
        level: null,
        office: null,
        first_name: "",
        last_name: "",
        id_card: "",
        tel: "",
        position: "",
        password: "",
        confirm_password: "",
        status: null,
        role: null
    }
    const { userData } = useUserContext()
    const toast = useToast()
    const { control, handleSubmit, formState: { errors }, watch, reset, setError } = useForm()
    const resetForm = () => {
        reset(defaultValues)
    }
    const onSubmit = async (data) => {
        // tel
        data.tel = data.tel?.replaceAll(" ", "").replaceAll("_", "")
        if (data.tel.length !== 10) {
            setError("tel", { type: 'invalid', message: 'รูปแบบหมายเลขโทรศัพท์ไม่ถูกต้อง' }, { shouldFocus: true })
            return;
        }

        // console.log(data)
        const newUser = new User(data)
        // console.log(newUser.serialized())

        addNewUser(newUser.serialized(), userData.token)
            .then(res => {
                resetForm()
                toast(toastVal({
                    title: "การบันทึกข้อมูลสำเร็จ",
                    description: `บันทึกข้อมูลการสมัครของ ${res.full_name} เรียบร้อยแล้ว`,
                }))
            })
            .catch(error => {
                toast(toastVal({
                    title: "การบันทึกข้อมูล ไม่สำเร็จ",
                    description: toastErrDesc(error),
                    status: "error",
                }))
            })
    }


    return (
        <Flex direction="column" w="70%" mt={4}>
            <form onSubmit={handleSubmit(onSubmit)}>
                <VStack spacing={3} mt={4} >
                    <SectionForm header="บัญชีผู้ใช้">
                        <FormControl isInvalid={errors.username} isRequired>
                            <FormLabel>ชื่อบัญชี (ใช้เลขบัตรประชาชนเป็นชื่อบัญชีสำหรับ สิทธิ์ผู้พักอาศัยและผู้ใช้งาน)</FormLabel>
                            <Controller
                                name="username"
                                control={control}
                                rules={{ required: true }}
                                render={({ field }) => <Input type="text" maxLength={100} placeholder="กรอกชื่อบัญชี" {...field} />}
                            />
                        </FormControl>
                        <FormControl isInvalid={errors.role} isRequired>
                            <FormLabel>สิทธิ์</FormLabel>
                            <SelectUserRole control={control} disabled={[1]} />
                        </FormControl>
                    </SectionForm>
                    <SectionForm header="ข้อมูลส่วนตัว">
                        <Stack w="full" direction={{ sm: "column", lg: "row" }}>
                            <FormControl isInvalid={errors.title} isRequired>
                                <FormLabel>คำนำหน้าชื่อ</FormLabel>
                                <SelectTitleName control={control} />
                                {errors.title && <FormErrorMessage>กรุณาเลือกคำนำหน้าชื่อ</FormErrorMessage>}
                            </FormControl>
                            <FormControl isInvalid={errors.first_name} isRequired>
                                <FormLabel>ชื่อ</FormLabel>
                                <Controller
                                    name="first_name"
                                    control={control}
                                    rules={{ required: true }}
                                    render={({ field }) => <Input type="text" maxLength={100} placeholder="กรอกชื่อ" {...field} />}
                                />
                            </FormControl>
                            <FormControl isInvalid={errors.last_name} isRequired>
                                <FormLabel>นามสกุล</FormLabel>
                                <Controller name="last_name"
                                    control={control}
                                    rules={{ required: true }}
                                    render={({ field }) => <Input type="text" maxLength={100} placeholder="กรอกนามสกุล" {...field} />}
                                />
                            </FormControl>
                        </Stack>
                        <FormControl isInvalid={errors.id_card} mt={2} isRequired>
                            <FormLabel>เลขบัตรประชาชน</FormLabel>
                            <InputIDCard control={control} />
                            {errors.id_card?.type === 'minLength' && <FormErrorMessage>ความยาวเลชบัตรประชาชนต้องเป็น 13 ตัวอักษร</FormErrorMessage>}
                        </FormControl>
                        <FormControl isRequired>
                            <FormLabel>หมายเลขโทรศัพท์มือถือ</FormLabel>
                            <InputTelephoneNumber control={control} />
                            <FormErrorMessage>{errors.tel?.message || "กรอกหมายเลขโทรศัพท์"}</FormErrorMessage>
                        </FormControl>
                    </SectionForm>
                    <SectionForm header="ตำแหน่งและหน่วยงาน">
                        <FormControl isInvalid={errors.position} isRequired>
                            <FormLabel>ชื่อตำแหน่ง</FormLabel>
                            <Controller name="position"
                                control={control}
                                rules={{ required: true }}
                                render={({ field }) => <Input type="text" maxLength={150} placeholder="กรอกชื่อตำแหน่ง" {...field} />}
                            />
                        </FormControl>
                        <FormControl mt={2} isInvalid={errors.level} isRequired>
                            <FormLabel>ระดับ</FormLabel>
                            <SelectLevel control={control} />
                            {errors.level && <FormErrorMessage>กรุณาเลือกระดับ</FormErrorMessage>}
                        </FormControl>
                        <FormControl mt={2} isInvalid={errors.office} isRequired>
                            <FormLabel>หน่วยงาน</FormLabel>
                            <SelectOffice control={control} />
                            {errors.office && <FormErrorMessage>กรุณาเลือกหน่วยงาน</FormErrorMessage>}
                        </FormControl>
                    </SectionForm>
                    <SectionForm header="ตั้งรหัสผ่าน" divider={false}>
                        <FormControl isInvalid={errors.password} isRequired>
                            <FormLabel>รหัสผ่าน</FormLabel>
                            <InputPassword control={control} name="password" placeholder="กรอกรหัสผ่าน 6-20 ตัวอักษร" />
                            {errors.password?.type === 'minLength' && <FormErrorMessage>ความยาวรหัสผ่านอย่างน้อย 6 ตัวอักษร</FormErrorMessage>}
                        </FormControl>
                        <FormControl mt={2} isInvalid={errors.confirm_password} isRequired>
                            <FormLabel>ยืนยันรหัสผ่าน</FormLabel>
                            <InputPassword control={control} name="confirm_password" validateValue={watch("password")} placeholder="กรอกรหัสผ่านอีกครั้ง" />
                            {errors.confirm_password?.type === 'validate' && <FormErrorMessage>ยืนยันรหัสผ่านไม่ตรงกัน</FormErrorMessage>}
                            {errors.confirm_password?.type === 'minLength' && <FormErrorMessage>ความยาวรหัสผ่านอย่างน้อย 6 ตัวอักษร</FormErrorMessage>}
                        </FormControl>
                    </SectionForm>
                </VStack>
                <VStack w="full">
                    <HStack mt={4}>
                        <Button type="submit" colorScheme="blue" fontSize="md" leftIcon={<BsBoxArrowInDown />}>
                            บันทึกข้อมูล
                        </Button>
                        <Button type="reset" colorScheme="yellow" fontSize="md" leftIcon={<BsArrowCounterclockwise />} onClick={() => resetForm()}>
                            ล้างฟอร์ม
                        </Button>
                    </HStack>
                </VStack>
            </form>
        </Flex>
    )
}

export default AddUserForm
